import Papa from 'papaparse'

import notify from './notify'

// Column type constants
export const COLUMN_TYPES = {
  VARCHAR: 'VARCHAR',
  BOOLEAN: 'BOOLEAN',
  INT: 'INT',
  LONG: 'LONG',
  FLOAT: 'FLOAT',
  DATE: 'DATE',
  TIME: 'TIME',
  TIMESTAMP: 'TIMESTAMP',
  BINARY: 'BINARY',
} as const

const DATE_FORMATS = {
  ISO: /^\d{4}-\d{2}-\d{2}$/,
  US: /^\d{1,2}\/\d{1,2}\/\d{4}$/,
} as const

const TIME_FORMATS = {
  WITH_SECONDS: /^\d{2}:\d{2}:\d{2}$/,
  WITHOUT_SECONDS: /^\d{2}:\d{2}$/,
  FLEXIBLE: /^\d{2}:\d{2}(:\d{2})?$/,
} as const

const TIMESTAMP_FORMATS = {
  ISO: /\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/,
  US: /^\d{1,2}\/\d{1,2}\/\d{4} \d{1,2}:\d{2}(:\d{2})?$/,
} as const

export interface CsvOptions {
  header?: 'first_line' | 'no_header'
  delimiter?: string
  whitespace?: 'trim' | 'preserve'
  enclosure?: string
  dateFormat?: string
  timeFormat?: string
  timestampFormat?: string
  errorHandling?: string
}

export interface ColumnType {
  name: string
  type: string
}

// Shared function to determine the type of a single value
const getValueType = (value: any): string => {
  if (typeof value === 'boolean') return COLUMN_TYPES.BOOLEAN

  if (!isNaN(Number(value))) {
    const num = Number(value)
    if (String(value).includes('.')) return COLUMN_TYPES.FLOAT
    if (Number.isInteger(num)) {
      // Check for leading zeros (but not for single zero)
      if (String(value).length > 1 && String(value).startsWith('0')) {
        return COLUMN_TYPES.VARCHAR
      }
      if (num >= -2147483648 && num <= 2147483647) return COLUMN_TYPES.INT
      return COLUMN_TYPES.LONG
    }
  }

  if (typeof value === 'string') {
    if (/^(true|false)$/i.test(value)) return COLUMN_TYPES.BOOLEAN
    if (TIME_FORMATS.FLEXIBLE.test(value)) return COLUMN_TYPES.TIME
    if (TIMESTAMP_FORMATS.ISO.test(value) || TIMESTAMP_FORMATS.US.test(value)) return COLUMN_TYPES.TIMESTAMP
    if (DATE_FORMATS.US.test(value) || DATE_FORMATS.ISO.test(value)) return COLUMN_TYPES.DATE
    if (/^(?:[A-Fa-f0-9]{2})+$/.test(value)) return COLUMN_TYPES.BINARY
  }

  return COLUMN_TYPES.VARCHAR
}

// Compare two types and return the more general one
const reconcileTypes = (currentType: string, newType: string): string => {
  if (currentType === newType) return currentType
  if (currentType === COLUMN_TYPES.VARCHAR || newType === COLUMN_TYPES.VARCHAR) return COLUMN_TYPES.VARCHAR

  if ((currentType === COLUMN_TYPES.DATE || currentType === COLUMN_TYPES.TIME) && newType === COLUMN_TYPES.TIMESTAMP)
    return COLUMN_TYPES.TIMESTAMP
  if (currentType === COLUMN_TYPES.TIMESTAMP && (newType === COLUMN_TYPES.DATE || newType === COLUMN_TYPES.TIME))
    return COLUMN_TYPES.TIMESTAMP

  if (currentType === COLUMN_TYPES.INT && newType === COLUMN_TYPES.LONG) return COLUMN_TYPES.LONG
  if (currentType === COLUMN_TYPES.LONG && newType === COLUMN_TYPES.INT) return COLUMN_TYPES.LONG
  if ((currentType === COLUMN_TYPES.INT || currentType === COLUMN_TYPES.LONG) && newType === COLUMN_TYPES.FLOAT)
    return COLUMN_TYPES.FLOAT
  if (currentType === COLUMN_TYPES.FLOAT && (newType === COLUMN_TYPES.INT || newType === COLUMN_TYPES.LONG))
    return COLUMN_TYPES.FLOAT

  // Incompatible types
  return COLUMN_TYPES.VARCHAR
}

export const guessColumnType = (values: any[]): string => {
  if (!values || values.length === 0) return COLUMN_TYPES.VARCHAR

  // Filter out null/empty values for analysis
  const nonEmptyValues = values.filter((v) => v != null && v !== '')
  if (nonEmptyValues.length === 0) return COLUMN_TYPES.VARCHAR

  let currentType: string | null = null

  for (const value of nonEmptyValues) {
    const valueType = getValueType(value)

    if (valueType === COLUMN_TYPES.VARCHAR) return COLUMN_TYPES.VARCHAR

    if (currentType === null) {
      currentType = valueType
    } else {
      const survivedType = reconcileTypes(currentType, valueType)
      if (survivedType === COLUMN_TYPES.VARCHAR) return COLUMN_TYPES.VARCHAR
      currentType = survivedType
    }
  }

  return currentType || COLUMN_TYPES.VARCHAR
}

export interface ParseCsvResult {
  data: Array<Record<string, any>>
  fields: string[]
  hasHeader: boolean
  guessedColumnTypes: ColumnType[]
}

export const parseCsvFile = (csvFile: File, options?: CsvOptions): Promise<ParseCsvResult> => {
  return new Promise((resolve, reject) => {
    // Do a "preview" parse first with 2 rows to determine if the file has a header
    Papa.parse(csvFile, {
      header: false,
      preview: 2,
      skipEmptyLines: true,
      complete: (previewResults) => {
        const hasHeader =
          options?.header === 'first_line' || (options?.header !== 'no_header' && options?.header === undefined)

        // Configure parse options based on provided options
        const parseConfig: Papa.ParseConfig = {
          header: hasHeader,
          skipEmptyLines: true,
          dynamicTyping: false,
          delimiter: options?.delimiter,
          // Apply other options when provided
          ...(options?.whitespace === 'trim' && { trimHeaders: true }),
          ...(options?.enclosure && { quoteChar: options.enclosure }),
        }

        // Use streaming approach for all files to check every row efficiently
        const columnTypes: Map<string, string> = new Map()
        const previewData: Array<Record<string, any>> = []
        let fields: string[] = []
        let isFirstChunk = true

        Papa.parse(csvFile, {
          ...parseConfig,
          worker: true, // Use web worker for better performance
          chunk: (results) => {
            const chunkData = results.data as Array<Record<string, any>>

            // Get fields from first chunk
            if (fields.length === 0) {
              fields = hasHeader
                ? results.meta.fields || []
                : chunkData.length > 0
                  ? Object.keys(chunkData[0]).map((key) => key as string)
                  : []

              // Initialize column types map (empty string = not yet determined)
              fields.forEach((field) => {
                columnTypes.set(field, '')
              })
            }

            chunkData.forEach((row, rowIndex) => {
              if (isFirstChunk && rowIndex === 0 && hasHeader) {
                return
              }

              if (previewData.length < 100) {
                previewData.push(row)
              }

              fields.forEach((field) => {
                const currentType = columnTypes.get(field) || ''
                if (currentType === COLUMN_TYPES.VARCHAR) {
                  // Already the most general type, no need to check further
                  return
                }

                const value = row[field]
                if (value == null || value === '') {
                  // Skip empty values
                  return
                }

                const valueType = getValueType(value)

                if (!currentType) {
                  columnTypes.set(field, valueType)
                } else if (currentType !== valueType) {
                  const newType = reconcileTypes(currentType, valueType)
                  columnTypes.set(field, newType)
                }
              })
            })

            isFirstChunk = false
          },
          complete: () => {
            if (!fields || fields.length === 0) {
              reject(new Error('Could not parse CSV: No columns found or invalid data.'))
              return
            }

            // Convert the tracked types to the expected format
            // Default to VARCHAR for columns with no non-empty values
            const guessedColumnTypes = fields.map((name) => {
              const type = columnTypes.get(name)
              return { name, type: type || COLUMN_TYPES.VARCHAR }
            })

            resolve({
              data: previewData,
              fields,
              hasHeader,
              guessedColumnTypes,
            })
          },
          error: (err) => {
            reject(err instanceof Error ? err : new Error(String(err)))
          },
        })
      },
      error: (err) => {
        reject(err instanceof Error ? err : new Error(String(err)))
      },
    })
  })
}

export const getDefaultCsvOptions = (): CsvOptions => ({
  header: undefined, // Let parseCsvFile's preview logic detect this
  delimiter: ',',
  whitespace: 'trim',
  enclosure: '"',
  dateFormat: 'auto',
  timeFormat: 'auto',
  timestampFormat: 'auto',
  errorHandling: 'stop',
})

export const processCsvFile = async (
  csvFile: File,
  form: any,
  fileName?: string,
  options: CsvOptions = getDefaultCsvOptions(),
) => {
  try {
    const { data, fields, hasHeader, guessedColumnTypes } = await parseCsvFile(csvFile, options)

    // Set all the form values (limit data  for preview)
    form.setFieldValue('hasHeader', hasHeader)
    form.setFieldValue('parsedCsvData', data.slice(0, 11))
    form.setFieldValue('parsedCsvFields', fields)
    form.setFieldValue('guessedColumnTypes', guessedColumnTypes)

    // Initialize csv options with detected values
    const detectedOptions = {
      header: hasHeader ? 'first_line' : 'no_header',
      delimiter: ',',
      whitespace: 'trim',
      enclosure: '"',
      dateFormat: 'auto',
      timeFormat: 'auto',
      timestampFormat: 'auto',
      errorHandling: 'stop',
    }

    form.setFieldValue('csvOptions', detectedOptions)
    form.setFieldValue('_lastProcessedOptions', detectedOptions)

    // Initialize column names and types from the guessed values
    form.setFieldValue(
      'columnNames',
      guessedColumnTypes.map((col) => col.name),
    )
    form.setFieldValue(
      'columnTypes',
      guessedColumnTypes.map((col) => col.type),
    )

    // Set the fileName if provided
    if (fileName) {
      form.setFieldValue('csvConfig.fileName', fileName)
    }

    return { success: true }
  } catch (err) {
    const errorMessage = `There was an error parsing your CSV file: ${err instanceof Error ? err.message : err}`
    notify.error(errorMessage)
    return { success: false, error: errorMessage }
  }
}

export const validateColumnTypeCompatibility = (values: any[], selectedType: string): boolean => {
  if (!values || values.length === 0) return true

  const nonEmptyValues = values.filter((v) => v != null && v !== '')
  if (nonEmptyValues.length === 0) return true

  for (const value of nonEmptyValues) {
    const detectedType = getValueType(value)

    if (selectedType === COLUMN_TYPES.VARCHAR) return true

    if (selectedType === detectedType) continue

    if (selectedType === COLUMN_TYPES.LONG && detectedType === COLUMN_TYPES.INT) continue
    if (
      selectedType === COLUMN_TYPES.FLOAT &&
      (detectedType === COLUMN_TYPES.INT || detectedType === COLUMN_TYPES.LONG)
    )
      continue
    if (
      selectedType === COLUMN_TYPES.TIMESTAMP &&
      (detectedType === COLUMN_TYPES.DATE || detectedType === COLUMN_TYPES.TIME)
    )
      continue

    return false
  }

  return true
}

export const validateDateTimeFormatCompatibility = (
  values: any[],
  selectedType: string,
  csvOptions: CsvOptions,
): boolean => {
  if (!['DATE', 'TIME', 'TIMESTAMP'].includes(selectedType)) return false

  return values.some((value: any) => {
    if (value == null || value === '') return false

    if (selectedType === 'DATE') {
      const dateFormat = csvOptions.dateFormat || 'auto'
      if (dateFormat === 'auto') return false
      if (dateFormat === 'yyyy-mm-dd' && !DATE_FORMATS.ISO.test(value)) return true
      if (dateFormat === 'mm/dd/yyyy' && !DATE_FORMATS.US.test(value)) return true
    }
    if (selectedType === 'TIME') {
      const timeFormat = csvOptions.timeFormat || 'auto'
      if (timeFormat === 'auto') return false
      if (timeFormat === 'hh:mm:ss' && !TIME_FORMATS.WITH_SECONDS.test(value)) return true
      if (timeFormat === 'hh:mm' && !TIME_FORMATS.WITHOUT_SECONDS.test(value)) return true
    }
    if (selectedType === 'TIMESTAMP') {
      const timestampFormat = csvOptions.timestampFormat || 'auto'
      if (timestampFormat === 'auto') return false
      if (timestampFormat === 'iso8601' && !TIMESTAMP_FORMATS.ISO.test(value)) return true
    }
    return false
  })
}
